import { TRAINING_ROLES } from "@/coquille-bridge";

/** Parcours formation Karaté Phase 1 — aide d'apprentissage par rôle. */

const rolePriority = {
  student: "students",
  parent: "parents",
  sensei: "managers",
  assistant: "managers",
  hanshi: "admin",
  admin: "admin",
};

export const trainingUiI18n = {
  fr: {
    pageTitle: "Formation",
    pageSubtitle: "Apprendre à utiliser GSD Karaté selon votre rôle — complémentaire au bouton Aide.",
    introTitle: "Aide d'apprentissage par rôle",
    introBody:
      "Le bouton Aide explique l'écran courant. L'onglet Formation explique le parcours complet d'un utilisateur : étudiant, parent, responsable ou administrateur.",
    loginHint: "Vous pouvez consulter les quatre formations. Connectez-vous pour voir aussi le parcours correspondant à votre rôle.",
    cardButton: "Voir la formation",
    selectedLabel: "Parcours ouvert",
    routesLabel: "Écrans concernés",
    knowTitle: "À savoir",
    limitsTitle: "[DÉMO] / limites Phase 1",
    checklistTitle: "Checklist rapide",
    detailStepsLead: "Suivez les étapes ci-dessous — liens directs vers les écrans concernés.",
    roleLabels: {
      player: "Pratiquant",
      manager: "Responsable",
      admin: "Administrateur",
    },
  },
  en: {
    pageTitle: "Training",
    pageSubtitle: "Learn how to use GSD Karate for your role — complementary to the Help button.",
    introTitle: "Role-based learning help",
    introBody:
      "The Help button explains the current screen. The Training tab explains the full user journey: student, parent, manager or administrator.",
    loginHint: "You can read all four training paths. Sign in to also highlight the path matching your role.",
    cardButton: "Open training",
    selectedLabel: "Open path",
    routesLabel: "Related screens",
    knowTitle: "Good to know",
    limitsTitle: "[DEMO] / Phase 1 limits",
    checklistTitle: "Quick checklist",
    detailStepsLead: "Follow the steps below — direct links to the related screens.",
    roleLabels: {
      player: "Member",
      manager: "Manager",
      admin: "Administrator",
    },
  },
};

export const trainingTracksByLocale = {
  fr: [
    {
      id: "students",
      role: TRAINING_ROLES.PLAYER,
      title: "Enfants / Étudiants",
      description: "Comprendre son espace personnel, ses documents, ses événements et ce qui est encore en démo.",
      duration: "10 min",
      sections: [
        {
          id: "student-dashboard",
          title: "Voir son tableau de bord",
          body:
            "Ouvrez Mon espace pour voir vos raccourcis : calendrier, adhésion, progression, examens, documents et Black Dragon.",
          links: [
            { label: "Mon espace", to: "/mon-espace" },
            { label: "Mon adhésion", to: "/mon-adhesion" },
            { label: "Mon calendrier", to: "/mon-calendrier" },
          ],
        },
        {
          id: "student-documents",
          title: "Consulter ses documents",
          body:
            "Mes documents montre uniquement les documents autorisés pour votre profil. Les documents d'autorité ou de preuve restent réservés aux responsables.",
          links: [
            { label: "Mes documents", to: "/mes-documents" },
            { label: "Documents publics", to: "/documents" },
          ],
        },
        {
          id: "student-communications",
          title: "Lire les communications",
          body:
            "En Phase 1, les communications étudiant sont présentées comme information de démonstration. L'envoi réel de messages viendra plus tard.",
          links: [{ label: "Aide", to: "/aide" }],
        },
        {
          id: "student-events",
          title: "Suivre ses événements",
          body:
            "Mes événements affiche les événements démo liés au dojo. Vérifiez toujours avec votre Sensei pour les horaires officiels.",
          links: [
            { label: "Mes cours", to: "/mes-cours" },
            { label: "Mes événements", to: "/mes-evenements" },
          ],
        },
        {
          id: "student-progress",
          title: "Comprendre sa progression",
          body:
            "Ma progression et Mes titres aident à suivre le parcours martial en mode lecture. Aucune certification officielle n'est produite par la démo.",
          links: [
            { label: "Ma progression", to: "/ma-progression" },
            { label: "Mes titres", to: "/mes-titres" },
          ],
        },
      ],
      knowItems: [
        "Utilisez les onglets simples en haut de votre espace : ils évitent les écrans administrateur.",
        "Les badges [DÉMO] indiquent qu'une donnée sert à illustrer le fonctionnement.",
        "Le bouton Aide reste utile pour comprendre l'écran ouvert.",
      ],
      limitItems: [
        "Documents, communications et événements sont en mode [DÉMO].",
        "Présences, paiements et validations officielles ne sont pas opérationnels en Phase 1.",
        "Les données officielles arriveront après validation du fichier client.",
      ],
      checklist: {
        title: "Checklist Étudiant",
        items: ["Ouvrir Mon espace", "Voir Mes documents", "Lire Mes événements", "Vérifier les badges [DÉMO]"],
      },
    },
    {
      id: "parents",
      role: TRAINING_ROLES.PLAYER,
      title: "Parents",
      description: "Suivre l'enfant, ses documents, communications, événements et les limites de la Phase 1.",
      duration: "12 min",
      sections: [
        {
          id: "parent-child",
          title: "Suivre les informations de l'enfant",
          body:
            "L'espace Parent regroupe les informations utiles pour votre enfant : résumé, horaires, événements, progression, documents et communications.",
          links: [
            { label: "Espace parent", to: "/parent" },
            { label: "Mes enfants", to: "/parent/mes-enfants" },
            { label: "Adhésion", to: "/parent/adhesion" },
          ],
        },
        {
          id: "parent-documents",
          title: "Voir les documents autorisés",
          body:
            "Documents affiche les éléments visibles pour le parent. Les dossiers de preuve et documents sensibles ne sont pas exposés dans cet espace.",
          links: [{ label: "Documents parent", to: "/parent/documents" }],
        },
        {
          id: "parent-comms",
          title: "Lire les communications",
          body:
            "Communications présente les messages démo du dojo. L'envoi et le suivi opérationnel des messages seront traités après la Phase 1.",
          links: [{ label: "Communications", to: "/parent/communications" }],
        },
        {
          id: "parent-events",
          title: "Suivre horaires et événements",
          body:
            "Horaires et Événements permettent de se repérer dans le calendrier de démonstration. Les présences ne sont pas encore opérationnelles.",
          links: [
            { label: "Horaires", to: "/parent/horaires" },
            { label: "Événements", to: "/parent/evenements" },
          ],
        },
        {
          id: "parent-progress",
          title: "Comprendre la progression",
          body:
            "Progression donne un aperçu du parcours martial de l'enfant, sans validation officielle de grade ou d'examen.",
          links: [{ label: "Progression", to: "/parent/progression" }],
        },
      ],
      knowItems: [
        "Le parent consulte, il ne valide pas les grades, titres ou documents.",
        "Les informations officielles doivent être confirmées par le dojo ou le Sensei.",
        "Les paiements ne sont pas gérés dans cette Phase 1.",
      ],
      limitItems: [
        "Données [DÉMO] uniquement.",
        "Paiements non gérés.",
        "Présences non opérationnelles.",
        "Documents sensibles et preuves non accessibles aux parents.",
      ],
      checklist: {
        title: "Checklist Parent",
        items: ["Ouvrir Espace parent", "Consulter Horaires", "Lire Communications", "Vérifier Documents parent"],
      },
    },
    {
      id: "managers",
      role: TRAINING_ROLES.MANAGER,
      title: "Responsables",
      description: "Former Sensei et Sempai : périmètre de consultation, gestion et validations interdites.",
      duration: "18 min",
      sections: [
        {
          id: "manager-difference",
          title: "Comprendre Sensei vs Sempai",
          body:
            "Le Sensei est responsable du dojo. Le Sempai assiste : il consulte, prépare et observe, mais ne valide pas les titres ni les grades.",
          links: [
            { label: "Espace Sensei", to: "/sensei" },
            { label: "Espace Sempai", to: "/sempai" },
          ],
        },
        {
          id: "manager-sensei",
          title: "Ce qu'un Sensei peut voir ou gérer",
          body:
            "Le Sensei voit son dojo, ses élèves, les cours, événements, présences légères, progression, examens et parcours Shotokan de son périmètre.",
          links: [
            { label: "Élèves Sensei", to: "/sensei/etudiants" },
            { label: "Progression Sensei", to: "/sensei/progression" },
          ],
        },
        {
          id: "manager-sempai",
          title: "Ce qu'un Sempai peut voir ou faire",
          body:
            "Le Sempai peut aider sur les classes, événements, présences légères, étudiants, progression et documents utiles en lecture ou préparation.",
          links: [
            { label: "Classes Sempai", to: "/sempai/classes" },
            { label: "Progression Sempai", to: "/sempai/progression" },
          ],
        },
        {
          id: "manager-no-validation",
          title: "Ce qu'un Sempai ne peut pas valider",
          body:
            "Un Sempai ne valide pas les titres, grades, dossiers de preuve ni publications sensibles. Ces décisions restent Sensei / Hanshi selon le contexte.",
          links: [{ label: "Parcours Sempai Shotokan", to: "/sempai/shotokan-black-dragon/parcours" }],
        },
        {
          id: "manager-docs-comms",
          title: "Communications, événements et documents utiles",
          body:
            "Les responsables utilisent les écrans cours, événements et documents pour préparer le dojo. Les communications réelles restent hors Phase 1.",
          links: [
            { label: "Documents Sensei", to: "/sensei/documents" },
            { label: "Documents Sempai", to: "/sempai/documents" },
          ],
        },
      ],
      knowItems: [
        "Le Sensei a un rôle de gestionnaire dojo.",
        "Le Sempai est un assistant : il aide sans valider les titres ou grades.",
        "Les écrans progression servent à préparer le suivi, pas à certifier officiellement en Phase 1.",
      ],
      limitItems: [
        "Présences légères seulement, non opérationnelles.",
        "Communications réelles non envoyées.",
        "Documents utiles en démo, sans backend sécurisé.",
        "Validation titre / grade réservée aux rôles autorisés, pas au Sempai.",
      ],
      checklist: {
        title: "Checklist Responsable",
        items: ["Comparer Sensei et Sempai", "Ouvrir Progression", "Ouvrir Documents utiles", "Repérer les limites [DÉMO]"],
      },
    },
    {
      id: "admin",
      role: TRAINING_ROLES.ADMIN,
      title: "Administrateur",
      description: "Former le Hanshi / admin principal sur la structure, Shotokan, Documents & Preuves et Phase 2.",
      duration: "30 min",
      sections: [
        {
          id: "admin-dashboard",
          title: "Tableau de bord Hanshi",
          body:
            "Le dashboard donne la vue d'ensemble Phase 1 : état démo, préparation client, compteurs et accès rapides.",
          links: [{ label: "Dashboard Hanshi", to: "/admin" }],
        },
        {
          id: "admin-organisation",
          title: "Organisations, dojos et personnes",
          body:
            "Organisation, Dojos, Instructeurs / Sensei, Assistants / Sempai, Étudiants et Parents servent à structurer les données avant import officiel.",
          links: [
            { label: "Organisation", to: "/admin/organisation" },
            { label: "Dojos", to: "/admin/dojos" },
            { label: "Instructeurs", to: "/admin/instructeurs" },
            { label: "Assistants", to: "/admin/assistants" },
            { label: "Étudiants", to: "/admin/etudiants" },
            { label: "Parents", to: "/admin/parents" },
          ],
        },
        {
          id: "admin-membership",
          title: "Gérer une inscription, un paiement et Black Dragon",
          body:
            "Le cockpit d'adhésion permet de vérifier un formulaire, suivre un paiement de démonstration, activer un membre et gérer les demandes Black Dragon. Un membre actif ne reçoit jamais de droits administratifs automatiquement.",
          links: [
            { label: "Membres officiels", to: "/admin/membres-officiels" },
            { label: "Cockpit d'adhésion", to: "/admin/adhesions" },
            { label: "Adhésions Black Dragon", to: "/admin/black-dragon-adhesions" },
          ],
        },
        {
          id: "admin-shotokan",
          title: "Shotokan Black Dragon",
          links: [{ label: "Shotokan Black Dragon", to: "/admin/shotokan-black-dragon" }],
        },
        {
          id: "admin-documents-preuves",
          title: "Documents & Preuves",
          body:
            "Documents & Preuves est dans Shotokan seulement. Le module présente documents, dossiers, validation, journal et sécurité future.",
          links: [{ label: "Documents & Preuves", to: "/admin/shotokan-black-dragon/documents-preuves" }],
        },
        {
          id: "admin-import",
          title: "Import contrôlé et données officielles",
          body:
            "Le fichier client officiel est attendu. L'import contrôlé montre un aperçu et un statut reçu — validation requise, sans document réel.",
          links: [
            { label: "Import CSV", to: "/admin/import" },
            { label: "Import contrôlé", to: "/admin/shotokan-black-dragon/documents-preuves/import" },
          ],
        },
        {
          id: "admin-phase2",
          title: "Ce qui viendra en Phase 2",
          body:
            "Phase 2 pourra couvrir données officielles validées, documents sécurisés, présences opérationnelles, paiements si requis et workflows complets.",
          links: [{ label: "Formation", to: "/formation" }],
        },
      ],
      knowItems: [
        "Le Hanshi/admin garde la vue complète et les validations sensibles.",
        "Documents & Preuves ne doit pas être présenté comme backend sécurisé en Phase 1.",
        "KARATE_MODELES_IMPORT_PHASE1.xlsx reste la référence pour le fichier client à remplir.",
      ],
      limitItems: [
        "Données officielles en attente du fichier client.",
        "Aucun document réel importé.",
        "Aucun paiement réel.",
        "Présences non opérationnelles.",
        "Backend documents sécurisé prévu plus tard.",
      ],
      checklist: {
        title: "Checklist Administrateur",
        items: [
          "Ouvrir Dashboard Hanshi",
          "Vérifier Organisation / Dojos / Instructeurs",
          "Ouvrir Shotokan Black Dragon",
          "Ouvrir Documents & Preuves",
          "Expliquer Phase 2",
        ],
      },
    },
  ],
  en: [
    {
      id: "students",
      role: TRAINING_ROLES.PLAYER,
      title: "Children / Students",
      description: "Understand your personal space, documents, events and what is still in demo mode.",
      duration: "10 min",
      sections: [
        {
          id: "student-dashboard",
          title: "View your dashboard",
          body:
            "Open My space to see shortcuts: calendar, membership, progress, exams, documents and Black Dragon.",
          links: [
            { label: "My space", to: "/mon-espace" },
            { label: "My membership", to: "/mon-adhesion" },
            { label: "My calendar", to: "/mon-calendrier" },
          ],
        },
        {
          id: "student-documents",
          title: "View your documents",
          body:
            "My documents shows only documents allowed for your profile. Authority or evidence documents stay reserved for managers.",
          links: [
            { label: "My documents", to: "/mes-documents" },
            { label: "Public documents", to: "/documents" },
          ],
        },
        {
          id: "student-communications",
          title: "Read communications",
          body:
            "In Phase 1, student communications are shown as demo information. Real message sending will come later.",
          links: [{ label: "Help", to: "/aide" }],
        },
        {
          id: "student-events",
          title: "Follow your events",
          body:
            "My events shows demo events linked to the dojo. Always confirm official schedules with your Sensei.",
          links: [
            { label: "My classes", to: "/mes-cours" },
            { label: "My events", to: "/mes-evenements" },
          ],
        },
        {
          id: "student-progress",
          title: "Understand your progress",
          body:
            "My progress and My titles help follow the martial path in read mode. No official certification is produced by the demo.",
          links: [
            { label: "My progress", to: "/ma-progression" },
            { label: "My titles", to: "/mes-titres" },
          ],
        },
      ],
      knowItems: [
        "Use the simple tabs at the top of your space: they avoid admin screens.",
        "[DEMO] badges mean the data illustrates how the app works.",
        "The Help button remains useful to understand the screen you are viewing.",
      ],
      limitItems: [
        "Documents, communications and events are in [DEMO] mode.",
        "Attendance, payments and official validations are not operational in Phase 1.",
        "Official data will arrive after the client file is validated.",
      ],
      checklist: {
        title: "Student checklist",
        items: ["Open My space", "View My documents", "Read My events", "Check [DEMO] badges"],
      },
    },
    {
      id: "parents",
      role: TRAINING_ROLES.PLAYER,
      title: "Parents",
      description: "Follow the child, documents, communications, events and Phase 1 limits.",
      duration: "12 min",
      sections: [
        {
          id: "parent-child",
          title: "Follow the child's information",
          body:
            "The Parent space groups useful information for your child: summary, schedule, events, progress, documents and communications.",
          links: [
            { label: "Parent space", to: "/parent" },
            { label: "My children", to: "/parent/mes-enfants" },
            { label: "Membership", to: "/parent/adhesion" },
          ],
        },
        {
          id: "parent-documents",
          title: "View allowed documents",
          body:
            "Documents shows items visible to the parent. Evidence dossiers and sensitive documents are not exposed in this space.",
          links: [{ label: "Parent documents", to: "/parent/documents" }],
        },
        {
          id: "parent-comms",
          title: "Read communications",
          body:
            "Communications shows demo dojo messages. Real sending and operational message tracking come after Phase 1.",
          links: [{ label: "Communications", to: "/parent/communications" }],
        },
        {
          id: "parent-events",
          title: "Follow schedule and events",
          body:
            "Schedule and Events help navigate the demo calendar. Attendance is not operational yet.",
          links: [
            { label: "Schedule", to: "/parent/horaires" },
            { label: "Events", to: "/parent/evenements" },
          ],
        },
        {
          id: "parent-progress",
          title: "Understand progress",
          body:
            "Progress gives an overview of the child's martial path, without official grade or exam validation.",
          links: [{ label: "Progress", to: "/parent/progression" }],
        },
      ],
      knowItems: [
        "Parents consult; they do not validate grades, titles or documents.",
        "Official information must be confirmed by the dojo or Sensei.",
        "Payments are not managed in this Phase 1.",
      ],
      limitItems: [
        "[DEMO] data only.",
        "Payments not managed.",
        "Attendance not operational.",
        "Sensitive documents and evidence are not accessible to parents.",
      ],
      checklist: {
        title: "Parent checklist",
        items: ["Open Parent space", "View Schedule", "Read Communications", "Check Parent documents"],
      },
    },
    {
      id: "managers",
      role: TRAINING_ROLES.MANAGER,
      title: "Managers",
      description: "Train Sensei and Sempai: consultation scope, management and forbidden validations.",
      duration: "18 min",
      sections: [
        {
          id: "manager-difference",
          title: "Understand Sensei vs Sempai",
          body:
            "The Sensei leads the dojo. The Sempai assists: they consult, prepare and observe, but do not validate titles or grades.",
          links: [
            { label: "Sensei space", to: "/sensei" },
            { label: "Sempai space", to: "/sempai" },
          ],
        },
        {
          id: "manager-sensei",
          title: "What a Sensei can see or manage",
          body:
            "The Sensei sees their dojo, students, classes, events, light attendance, progress, exams and Shotokan path for their scope.",
          links: [
            { label: "Sensei students", to: "/sensei/etudiants" },
            { label: "Sensei progress", to: "/sensei/progression" },
          ],
        },
        {
          id: "manager-sempai",
          title: "What a Sempai can see or do",
          body:
            "The Sempai can help on classes, events, light attendance, students, progress and useful documents in read or preparation mode.",
          links: [
            { label: "Sempai classes", to: "/sempai/classes" },
            { label: "Sempai progress", to: "/sempai/progression" },
          ],
        },
        {
          id: "manager-no-validation",
          title: "What a Sempai cannot validate",
          body:
            "A Sempai does not validate titles, grades, evidence dossiers or sensitive publication. Those decisions stay with Sensei / Hanshi depending on context.",
          links: [{ label: "Sempai Shotokan path", to: "/sempai/shotokan-black-dragon/parcours" }],
        },
        {
          id: "manager-docs-comms",
          title: "Communications, events and useful documents",
          body:
            "Managers use classes, events and documents screens to prepare dojo work. Real communications remain outside Phase 1.",
          links: [
            { label: "Sensei documents", to: "/sensei/documents" },
            { label: "Sempai documents", to: "/sempai/documents" },
          ],
        },
      ],
      knowItems: [
        "The Sensei has a dojo manager role.",
        "The Sempai is an assistant: they help without validating titles or grades.",
        "Progress screens prepare follow-up; they do not officially certify in Phase 1.",
      ],
      limitItems: [
        "Light attendance only, not operational.",
        "Real communications are not sent.",
        "Useful documents are demo only, with no secure backend.",
        "Title / grade validation is reserved for authorized roles, not the Sempai.",
      ],
      checklist: {
        title: "Manager checklist",
        items: ["Compare Sensei and Sempai", "Open Progress", "Open Useful documents", "Identify [DEMO] limits"],
      },
    },
    {
      id: "admin",
      role: TRAINING_ROLES.ADMIN,
      title: "Administrator",
      description: "Train the Hanshi / main admin on structure, Shotokan, Documents & Evidence and Phase 2.",
      duration: "30 min",
      sections: [
        {
          id: "admin-dashboard",
          title: "Hanshi dashboard",
          body:
            "The dashboard gives the Phase 1 overview: demo status, client preparation, counters and quick access.",
          links: [{ label: "Hanshi dashboard", to: "/admin" }],
        },
        {
          id: "admin-organisation",
          title: "Organizations, dojos and people",
          body:
            "Organisation, Dojos, Instructors / Sensei, Assistants / Sempai, Students and Parents structure data before official import.",
          links: [
            { label: "Organisation", to: "/admin/organisation" },
            { label: "Dojos", to: "/admin/dojos" },
            { label: "Instructors", to: "/admin/instructeurs" },
            { label: "Assistants", to: "/admin/assistants" },
            { label: "Students", to: "/admin/etudiants" },
            { label: "Parents", to: "/admin/parents" },
          ],
        },
        {
          id: "admin-membership",
          title: "Manage a registration, a payment and Black Dragon",
          body:
            "The membership cockpit lets you review a form, follow a demo payment, activate a member and manage Black Dragon requests. An active member never receives administrative rights automatically.",
          links: [
            { label: "Official members", to: "/admin/membres-officiels" },
            { label: "Membership cockpit", to: "/admin/adhesions" },
            { label: "Black Dragon memberships", to: "/admin/black-dragon-adhesions" },
          ],
        },
        {
          id: "admin-shotokan",
          title: "Shotokan Black Dragon",
          links: [{ label: "Shotokan Black Dragon", to: "/admin/shotokan-black-dragon" }],
        },
        {
          id: "admin-documents-preuves",
          title: "Documents & Evidence",
          body:
            "Documents & Evidence sits inside Shotokan only. It presents documents, dossiers, validation, log and future security.",
          links: [{ label: "Documents & Evidence", to: "/admin/shotokan-black-dragon/documents-preuves" }],
        },
        {
          id: "admin-import",
          title: "Controlled import and official data",
          body:
            "The official client file is pending. Controlled import shows a preview and received — validation required status, without real documents.",
          links: [
            { label: "CSV import", to: "/admin/import" },
            { label: "Controlled import", to: "/admin/shotokan-black-dragon/documents-preuves/import" },
          ],
        },
        {
          id: "admin-phase2",
          title: "What comes in Phase 2",
          body:
            "Phase 2 may cover validated official data, secure documents, operational attendance, payments if required and complete workflows.",
          links: [{ label: "Training", to: "/formation" }],
        },
      ],
      knowItems: [
        "The Hanshi/admin keeps the full view and sensitive validations.",
        "Documents & Evidence must not be presented as a secure backend in Phase 1.",
        "KARATE_MODELES_IMPORT_PHASE1.xlsx remains the reference for the client file to complete.",
      ],
      limitItems: [
        "Official data awaits the client file.",
        "No real document imported.",
        "No real payment.",
        "Attendance not operational.",
        "Secure documents backend planned later.",
      ],
      checklist: {
        title: "Administrator checklist",
        items: [
          "Open Hanshi dashboard",
          "Check Organisation / Dojos / Instructors",
          "Open Shotokan Black Dragon",
          "Open Documents & Evidence",
          "Explain Phase 2",
        ],
      },
    },
  ],
};

function normalizeLocale(locale) {
  return locale === "en" ? "en" : "fr";
}

export function getTrainingUi(locale = "fr") {
  return trainingUiI18n[normalizeLocale(locale)];
}

export function getAllTrainingTracks(locale = "fr") {
  return trainingTracksByLocale[normalizeLocale(locale)];
}

/** Compatibilité tests historiques : contenu FR par défaut. */
export const trainingTracks = getAllTrainingTracks("fr");

export function getTrainingTracksForRole(role, locale = "fr") {
  const tracks = getAllTrainingTracks(locale);
  const priorityId = rolePriority[role] || "students";
  return [...tracks].sort((a, b) => {
    if (a.id === priorityId) return -1;
    if (b.id === priorityId) return 1;
    return 0;
  });
}
